import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import DateInput from "./ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hoy = () =>
  new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

/**
 * ChequeEgresoForm
 *
 * Formulario reutilizable para emitir un cheque vinculado a un egreso.
 * Flujo: seleccionar talonario → ver próxima hoja → completar datos → emitir.
 *
 * Props:
 *   token        — JWT del usuario
 *   logoTipo     — empresa activa (slug)
 *   beneficiario — pre-llenado (nombre del proveedor / empleado)
 *   monto        — pre-llenado (número)
 *   egresoMoneda — moneda del egreso ("PYG" | "USD" | "BRL"). Muestra campo TC si difiere del cheque.
 *   egresoTipo   — "compra" | "costo_fijo" | "sueldo"
 *   egresoId     — id del egreso a vincular
 *   onSuccess(hoja, chequera, tipoCambio) — llamado tras emitir con éxito.
 *                 Si el paso posterior (registrar el pago/egreso vinculado)
 *                 falla, onSuccess DEBE relanzar el error (throw) en vez de
 *                 solo mostrar un toast — así este formulario sabe que el
 *                 cheque quedó "emitido" sin ningún pago real detrás y lo
 *                 revierte automáticamente a disponible, en vez de dejarlo
 *                 huérfano (eso era lo que hacía que, al reintentar, el
 *                 backend rechazara la emisión diciendo que la hoja ya
 *                 estaba emitida/diferida, y el usuario quedaba trabado en
 *                 la pantalla de emisión sin poder avanzar).
 *   onCancel     — cerrar sin emitir
 */
export default function ChequeEgresoForm({
  token,
  logoTipo,
  beneficiario = "",
  monto = "",
  egresoMoneda = "PYG",
  cuentaId = "",
  egresoTipo,
  egresoId,
  onSuccess,
  onCancel,
}) {
  const [chequeras, setChequeras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChequera, setSelectedChequera] = useState(null);
  const [nextHoja, setNextHoja] = useState(null);
  const [hojasDisp, setHojasDisp] = useState([]);
  const [loadingHoja, setLoadingHoja] = useState(false);
  const [form, setForm] = useState({
    nombre_beneficiario: beneficiario,
    ruc_beneficiario: "",
    valor: monto ? String(monto) : "",
    fecha_emision: hoy(),
    fecha_pago: hoy(),
    concepto: "",
  });
  const [tipoCambio, setTipoCambio] = useState("");
  const [saving, setSaving] = useState(false);
  const preseleccionar = useRef(null);

  useEffect(() => {
    const fetchChequeras = async () => {
      try {
        const params = logoTipo ? `?logo_tipo=${logoTipo}` : "";
        const res = await fetch(`${API}/admin/cheques/chequeras${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const disp = data.filter((ch) => (ch.conteo_disponible || 0) > 0);
          setChequeras(disp);
          // Si el pago ya tiene cuenta bancaria elegida, se preselecciona su talonario
          const delaCuenta = cuentaId ? disp.filter((ch) => ch.cuenta_bancaria_id === cuentaId) : [];
          if (delaCuenta.length === 1) preseleccionar.current = delaCuenta[0].id;
        }
      } catch {}
      setLoading(false);
    };
    fetchChequeras().then(() => {
      if (preseleccionar.current) handleSelectChequera(preseleccionar.current);
    });
  }, [token, logoTipo]);

  const handleSelectChequera = async (chequeraId) => {
    const ch = chequeras.find((c) => c.id === chequeraId);
    setSelectedChequera(ch || null);
    setNextHoja(null);
    setHojasDisp([]);
    if (!ch) return;
    setLoadingHoja(true);
    try {
      const res = await fetch(
        `${API}/admin/cheques/hojas?chequera_id=${ch.id}&estado=disponible&limit=200${logoTipo ? `&logo_tipo=${logoTipo}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const lista = data.hojas || [];
        setHojasDisp(lista);
        // Sugerida: la primera disponible (menor número); se puede elegir otra
        setNextHoja(lista[0] || null);
      }
    } catch {}
    setLoadingHoja(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nextHoja) {
      toast.error("No hay hoja disponible en este talonario");
      return;
    }
    setSaving(true);
    let hojaEmitida = null;
    try {
      const res = await fetch(
        `${API}/admin/cheques/hojas/${nextHoja.id}/emitir`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...form,
            valor: parseFloat(form.valor),
            logo_tipo: logoTipo,
            egreso_tipo: egresoTipo,
            egreso_id: egresoId,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(
          typeof data.detail === "string" ? data.detail : "Error al emitir cheque"
        );
        return;
      }
      hojaEmitida = data.hoja;
      await onSuccess(data.hoja, selectedChequera, tipoCambio ? parseFloat(tipoCambio) : null);
    } catch (err) {
      if (hojaEmitida) {
        // El cheque ya se emitió pero el pago/egreso que lo iba a usar no se
        // completó (onSuccess relanzó el error). No se llegó a entregar
        // ningún cheque real, así que se revierte la hoja a disponible en
        // vez de dejarla huérfana como "emitida" sin nada que la respalde.
        try {
          await fetch(`${API}/admin/cheques/hojas/${hojaEmitida.id}/revertir-emision`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ logo_tipo: logoTipo }),
          });
          toast("El cheque no se llegó a usar — se liberó de nuevo, podés reintentar.", { icon: "↩️" });
        } catch {
          toast.error("El cheque quedó emitido pero el pago no se registró. Anulalo manualmente en Cheques antes de reintentar.");
        }
        // Refrescar la hoja disponible del mismo talonario para reintentar
        // sin salir de esta pantalla.
        if (selectedChequera) handleSelectChequera(selectedChequera.id);
      } else {
        toast.error("Error al emitir cheque");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 gap-2 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Cargando talonarios...
      </div>
    );
  }

  if (chequeras.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-slate-400 text-sm mb-1">
          Sin talonarios con cheques disponibles
        </p>
        <p className="text-slate-600 text-xs">
          Registrá un talonario en Financiero → Cheques
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 px-4 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-sm transition-colors"
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Selector talonario */}
      <div>
        <label className="text-slate-400 text-xs mb-1 block">
          Talonario de cheques *
        </label>
        <select
          value={selectedChequera?.id || ""}
          onChange={(e) => handleSelectChequera(e.target.value)}
          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
        >
          <option value="">Seleccionar talonario...</option>
          {chequeras.map((ch) => (
            <option key={ch.id} value={ch.id}>
              {ch.banco_nombre} — Serie {ch.serie} ({ch.conteo_disponible} disp.)
            </option>
          ))}
        </select>
      </div>

      {selectedChequera && (
        <>
          {loadingHoja ? (
            <div className="flex items-center gap-2 text-slate-400 text-xs py-1 animate-pulse">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando próximo
              cheque...
            </div>
          ) : nextHoja ? (
            <>
              <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg px-3 py-2 text-xs text-emerald-300 space-y-1">
                <label className="block">
                  Cheque a usar — {selectedChequera.banco_nombre}{" "}
                  <span className="text-emerald-400/70">(sugerido: {hojasDisp[0]?.serie}{String(hojasDisp[0]?.numero ?? "").padStart(6, "0")})</span>
                </label>
                <select
                  value={nextHoja.id}
                  onChange={(e) => setNextHoja(hojasDisp.find((h) => h.id === e.target.value) || nextHoja)}
                  style={{ colorScheme: "dark" }}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                >
                  {hojasDisp.map((h, i) => (
                    <option key={h.id} value={h.id}>
                      {h.serie}{String(h.numero).padStart(6, "0")}{i === 0 ? " (sugerido)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">
                    Beneficiario *
                  </label>
                  <input
                    value={form.nombre_beneficiario}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nombre_beneficiario: e.target.value }))
                    }
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    placeholder="Nombre o razón social"
                    required
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">
                    RUC / CI
                  </label>
                  <input
                    value={form.ruc_beneficiario}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, ruc_beneficiario: e.target.value }))
                    }
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">
                    Valor ({nextHoja.moneda}) *
                  </label>
                  <input
                    type="number"
                    value={form.valor}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, valor: e.target.value }))
                    }
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    placeholder="0"
                    min={0.01}
                    step="any"
                    required
                  />
                </div>
                {/* Tipo de cambio: visible cuando moneda del cheque ≠ moneda del egreso */}
                {selectedChequera?.moneda && egresoMoneda && selectedChequera.moneda !== egresoMoneda && (
                  <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3 space-y-2">
                    <p className="text-amber-300 text-xs">
                      El cheque es en <strong>{selectedChequera.moneda}</strong> pero el egreso está en <strong>{egresoMoneda}</strong>. Ingresá el tipo de cambio.
                    </p>
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">
                        Tipo de cambio ({selectedChequera.moneda} → {egresoMoneda}) *
                      </label>
                      <input
                        type="number"
                        value={tipoCambio}
                        onChange={(e) => setTipoCambio(e.target.value)}
                        className="w-full bg-arandu-dark border border-amber-500/40 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-400"
                        placeholder="Ej: 7500"
                        min={0.000001}
                        step="any"
                        required
                      />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">
                      Fecha emisión *
                    </label>
                    <DateInput
                      value={form.fecha_emision}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, fecha_emision: e.target.value }))
                      }
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">
                      Fecha pago *
                    </label>
                    <DateInput
                      value={form.fecha_pago}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, fecha_pago: e.target.value }))
                      }
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                      required
                    />
                  </div>
                </div>
                {form.fecha_pago && form.fecha_emision && (
                  <div
                    className={`text-xs px-3 py-2 rounded-lg ${
                      form.fecha_pago > form.fecha_emision
                        ? "bg-amber-900/20 text-amber-300"
                        : "bg-emerald-900/20 text-emerald-300"
                    }`}
                  >
                    {form.fecha_pago > form.fecha_emision
                      ? `⚠ Cheque diferido — pagadero el ${form.fecha_pago}`
                      : "✓ Cheque al día"}
                  </div>
                )}
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">
                    Concepto
                  </label>
                  <input
                    value={form.concepto}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, concepto: e.target.value }))
                    }
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    placeholder="Descripción opcional"
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    {saving ? "Emitiendo…" : "Emitir y pagar"}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-300">
              No hay hojas disponibles en este talonario. Registrá un talonario nuevo.
            </div>
          )}
        </>
      )}
    </div>
  );
}
